import React, { useCallback, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled, List, ListItem, IconButton as MuiIconButton } from '@mui/material'
import { FACE_ICONS, FACE_NAMES, FACE_ARIA_LABELS } from './constants'
import { ExperienceRating } from './types'
import { getIconColor, getTabIndex, getNextIndex } from './helpers'

export interface Props {
  selectedRate: ExperienceRating
  onChange: (value: ExperienceRating) => void
  disabled?: boolean
  'aria-labelledby'?: string
}

const ExperienceRatingScale: React.FC<Props> = ({
  selectedRate,
  onChange,
  disabled = false,
  'aria-labelledby': ariaLabelledBy
}) => {
  const [hovered, setHovered] = useState(false)
  const radioRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (disabled) return

      const nextIndex = getNextIndex(e.key, index)

      if (nextIndex !== null) {
        e.preventDefault()
        setFocusedIndex(nextIndex)
        radioRefs.current[nextIndex]?.focus()
      }
    },
    [disabled]
  )

  const handleFocus = useCallback((index: number) => {
    setFocusedIndex(index)
  }, [])

  const handleBlur = useCallback(() => {
    setFocusedIndex(-1)
  }, [])

  const handleClick = useCallback(
    (faceType: ExperienceRating) => {
      if (disabled) return
      onChange(selectedRate === faceType ? null : faceType)
    },
    [disabled, onChange, selectedRate]
  )

  return (
    <List
      role="list"
      aria-labelledby={ariaLabelledBy}
      disablePadding
      sx={{ display: 'flex' }}
    >
      {FACE_NAMES.map((faceType, index) => (
        <ListItem key={faceType} role="listitem" sx={{ width: 'auto', padding: 1 }}>
          <MuiIconButton
            ref={(el: HTMLButtonElement | null) => {
              radioRefs.current[index] = el
            }}
            type="button"
            disabled={disabled}
            onClick={() => handleClick(faceType)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, index)}
            onFocus={() => handleFocus(index)}
            onBlur={handleBlur}
            aria-label={FACE_ARIA_LABELS[faceType]}
            aria-pressed={selectedRate === faceType}
            tabIndex={getTabIndex(
              index,
              faceType,
              focusedIndex,
              selectedRate,
              disabled
            )}
            sx={{ width: '30px', height: '30px', padding: 0 }}
          >
            <FaceIcon
              disabled={disabled}
              hovered={hovered}
              iconName={faceType}
              icon={FACE_ICONS[faceType]}
              isSelected={selectedRate === faceType}
            />
          </MuiIconButton>
        </ListItem>
      ))}
    </List>
  )
}

const FaceIcon = styled(FontAwesomeIcon, {
  shouldForwardProp: prop => !['hovered', 'isSelected', 'iconName'].includes(prop as string),
})<{
  hovered: boolean
  isSelected: boolean
  iconName: ExperienceRating
  disabled: boolean
}>(({ theme, isSelected, hovered, iconName, disabled = false }) => ({
  fontSize: '28px',
  color: getIconColor(isSelected, iconName, theme.palette),
  transition: 'color 0.5s ease-in-out',
  cursor: 'pointer',
  ...(disabled && { pointerEvents: 'none' }),

  ...(hovered
    ? {
        color: theme.palette.blue[200],

        '&:hover': {
          color: theme.palette.blue[500],
        },
      }
    : {}),
}))

export default ExperienceRatingScale
